/**
 * Text Parser Service
 *
 * Centralized utilities for parsing and manipulating drawing designation text.
 * Handles extraction of prefix, designation, and value from keyed text format.
 */

import {
    DESIGNATION_PLACEHOLDER,
    SERVER_DESIGNATION_PLACEHOLDER,
} from '../constants'

/**
 * Parsed text structure for Product Type with drawing designation
 */
export interface ParsedDesignationText {
    /** Text before the parentheses (e.g., "A. Clear Annealed Float Glass") */
    prefix: string
    /** Drawing designation value inside parentheses (empty if placeholder) */
    designation: string
    /** Value after the colon (e.g., "ASTM C1036...") */
    value: string
    /** Full original text */
    fullText: string
}

/**
 * Options for parsing designation text
 */
export interface ParseDesignationOptions {
    /** Whether to treat placeholder as empty string (default: true) */
    treatPlaceholderAsEmpty?: boolean
    /** Raw designation from the server — when provided, used to precisely locate the
     *  designation in text instead of heuristic parsing */
    serverDesignation?: string
}

/** Checks whether `text` matches a known designation placeholder (exact or case-insensitive). */
const isDesignationPlaceholder = (text: string): boolean =>
    text === DESIGNATION_PLACEHOLDER ||
    text.toLowerCase() === SERVER_DESIGNATION_PLACEHOLDER.toLowerCase()

/** Splits `text` at the first colon. Returns the full text as prefix when no colon exists. */
const splitAtColon = (text: string): { prefix: string; value: string } => {
    const colonIndex = text.indexOf(':')
    return colonIndex !== -1
        ? {
              prefix: text.substring(0, colonIndex).trim(),
              value: text.substring(colonIndex + 1).trim(),
          }
        : { prefix: text.trim(), value: '' }
}

/** Builds a `ParsedDesignationText` result from the four constituent parts. */
const makeResult = (
    prefix: string,
    designation: string,
    value: string,
    fullText: string
): ParsedDesignationText => ({ prefix, designation, value, fullText })

/**
 * Finds the exact `(designation)` pattern in text using lastIndexOf so that
 * parenthesized prefix names (e.g. "Expansion (Control) Joint") are preserved.
 * Returns null if the pattern is not found.
 */
const findEmbeddedDesignation = (
    text: string,
    designation: string
): { prefix: string; value: string } | null => {
    const pattern = `(${designation})`
    const index = text.lastIndexOf(pattern)
    if (index === -1) return null

    const prefix = text.substring(0, index).trim()
    const afterPattern = text.substring(index + pattern.length)
    const colonIndex = afterPattern.indexOf(':')
    const value =
        colonIndex !== -1 ? afterPattern.substring(colonIndex + 1).trim() : ''
    return { prefix, value }
}

/**
 * Finds a `(placeholder):` pattern in text where the parenthesized content is
 * a known designation placeholder. Returns null if no such pattern exists.
 */
const findPlaceholderInParens = (
    text: string
): { prefix: string; value: string } | null => {
    const openParen = text.indexOf('(')
    if (openParen === -1) return null

    const closeParenColon = text.indexOf('):', openParen)
    if (closeParenColon === -1) return null

    const parenContent = text.substring(openParen + 1, closeParenColon).trim()
    if (!isDesignationPlaceholder(parenContent)) return null

    return {
        prefix: text.substring(0, openParen).trim(),
        value: text.substring(closeParenColon + 2).trim(),
    }
}

/**
 * Splits text at the colon that follows the last `)`, preserving parenthesized
 * prefix names like "Expansion (Control) Joint:".
 */
const splitPreservingPrefixParens = (
    text: string
): { prefix: string; value: string } => {
    const lastCloseParen = text.lastIndexOf(')')
    const colonIndex =
        lastCloseParen !== -1
            ? text.indexOf(':', lastCloseParen)
            : text.indexOf(':')
    return colonIndex !== -1
        ? {
              prefix: text.substring(0, colonIndex).trim(),
              value: text.substring(colonIndex + 1).trim(),
          }
        : { prefix: text.trim(), value: '' }
}

/**
 * Removes a trailing `(Product Designation)` or `(Insert drawing designation)`
 * suffix from a prefix string.
 */
const stripTrailingPlaceholder = (prefix: string): string => {
    const placeholders = [
        DESIGNATION_PLACEHOLDER,
        SERVER_DESIGNATION_PLACEHOLDER,
    ]
    for (const ph of placeholders) {
        const suffix = `(${ph})`
        if (prefix.toLowerCase().endsWith(suffix.toLowerCase())) {
            return prefix.substring(0, prefix.length - suffix.length).trim()
        }
    }
    return prefix
}

// ── Existing public utilities ───────────────────────────────────────────

/**
 * Finds the matching closing parenthesis for an opening parenthesis.
 * Handles nested parentheses correctly.
 *
 * @param text - The text to search
 * @param openIndex - Index of the opening parenthesis
 * @returns Index of matching closing parenthesis, or -1 if not found
 */
const findMatchingCloseParen = (text: string, openIndex: number): number => {
    let depth = 1
    for (let i = openIndex + 1; i < text.length; i++) {
        if (text[i] === '(') depth++
        if (text[i] === ')') depth--
        if (depth === 0) return i
    }
    return -1
}

/**
 * Finds the index of the delimiter colon that separates the key from the value.
 * For text with designation format like "Prefix (designation): value", finds the
 * colon AFTER the closing parenthesis, not colons inside the designation.
 *
 * Uses the `):` pattern to reliably find the delimiter, which handles designations
 * containing unbalanced parentheses like "() try: with colon (".
 *
 * @param text - The text to search
 * @param serverDesignation - Optional server designation for exact pattern matching,
 *   avoiding false "):' matches inside the designation (Bug 2606922)
 * @returns Index of the delimiter colon, or -1 if not found
 */
export const findDelimiterColonIndex = (
    text: string,
    serverDesignation?: string
): number => {
    // When server designation is available, use exact pattern matching (lastIndexOf)
    // to avoid false positives when the designation itself contains "):"
    if (serverDesignation) {
        const pattern = `(${serverDesignation}):`
        const patternIndex = text.lastIndexOf(pattern)

        if (patternIndex !== -1) {
            return patternIndex + pattern.length - 1
        }
    }

    const openParenIndex = text.indexOf('(')

    // No parentheses - find first colon as usual
    if (openParenIndex === -1) {
        return text.indexOf(':')
    }

    // This reliably finds the end of the designation regardless of nested/unbalanced parens
    const closeParenColonIndex = text.indexOf('):', openParenIndex)
    if (closeParenColonIndex !== -1) {
        return closeParenColonIndex + 1 // +1 to point to the `:` after `)`
    }

    // Has `(` but no `):` - check for colon BEFORE the open paren
    const colonBeforeParen = text.lastIndexOf(':', openParenIndex)
    if (colonBeforeParen !== -1) {
        return colonBeforeParen
    }

    return -1
}

/**
 * Checks if text contains the delimiter colon (after any designation parentheses).
 * This handles colons inside designations like "DD:001" correctly.
 *
 * @param text - The text to check
 * @returns true if text contains the delimiter colon
 */
export const hasDelimiterColon = (text: string): boolean => {
    return findDelimiterColonIndex(text) !== -1
}

/**
 * Parses keyed text with drawing designation format.
 *
 * Expected format: "{prefix} ({designation}): {value}"
 * Example: "A. Clear Annealed Float Glass (insert drawing designation): ASTM C1036..."
 *
 * The designation is always within parentheses before the colon.
 * Handles colons and unbalanced parentheses inside the designation correctly
 * by using the `):` pattern to find the end of designation.
 *
 * @param text - The text to parse
 * @param options - Parsing options
 * @returns Parsed structure with prefix, designation, and value
 */
export const parseDesignationText = (
    text: string,
    options: ParseDesignationOptions = {}
): ParsedDesignationText => {
    const { treatPlaceholderAsEmpty = true, serverDesignation } = options

    // Strategy A: Server-provided designation for precise splitting
    if (serverDesignation !== undefined) {
        if (serverDesignation) {
            // A1: Find exact "(designation)" in text (lastIndexOf preserves prefix parens)
            const embedded = findEmbeddedDesignation(text, serverDesignation)
            if (embedded) {
                return makeResult(
                    embedded.prefix,
                    serverDesignation,
                    embedded.value,
                    text
                )
            }

            // A2: Text may have a placeholder — strip it if found
            const placeholder = findPlaceholderInParens(text)
            if (placeholder) {
                return makeResult(
                    placeholder.prefix,
                    serverDesignation,
                    placeholder.value,
                    text
                )
            }

            // A3: Parens are part of the prefix name — preserve them
            if (text.indexOf('(') !== -1) {
                const parts = splitPreservingPrefixParens(text)
                return makeResult(
                    parts.prefix,
                    serverDesignation,
                    parts.value,
                    text
                )
            }

            // A4: No parens at all — simple colon split
            const parts = splitAtColon(text)
            return makeResult(
                parts.prefix,
                serverDesignation,
                parts.value,
                text
            )
        }

        // A5: Empty designation — colon split then strip trailing placeholder from prefix
        const parts = splitAtColon(text)
        return makeResult(
            stripTrailingPlaceholder(parts.prefix),
            '',
            parts.value,
            text
        )
    }

    // Strategy B: Heuristic parsing (no serverDesignation)
    const openParenIndex = text.indexOf('(')

    // B1: No parentheses — simple colon split
    if (openParenIndex === -1) {
        const parts = splitAtColon(text)
        return makeResult(parts.prefix, '', parts.value, text)
    }

    // B2: Has `):` pattern — extract designation from parens
    const closeParenColonIndex = text.indexOf('):', openParenIndex)
    if (closeParenColonIndex !== -1) {
        const prefix = text.substring(0, openParenIndex).trim()
        let designation = text
            .substring(openParenIndex + 1, closeParenColonIndex)
            .trim()
        const value = text.substring(closeParenColonIndex + 2).trim()

        if (treatPlaceholderAsEmpty && isDesignationPlaceholder(designation)) {
            designation = ''
        }

        return makeResult(prefix, designation, value, text)
    }

    // B3: No `):` — try paren matching for "Prefix (designation)" format (no value)
    const closeParenIndex = findMatchingCloseParen(text, openParenIndex)
    if (closeParenIndex !== -1) {
        const prefix = text.substring(0, openParenIndex).trim()
        let designation = text
            .substring(openParenIndex + 1, closeParenIndex)
            .trim()

        if (treatPlaceholderAsEmpty && isDesignationPlaceholder(designation)) {
            designation = ''
        }

        return makeResult(prefix, designation, '', text)
    }

    // B4: Fallback — simple colon split
    const parts = splitAtColon(text)
    return makeResult(parts.prefix, '', parts.value, text)
}

/**
 * Removes designation values in parentheses from text.
 *
 * Prevents duplicate designations in mutations.
 * Only removes parentheses content that appears to be a designation (directly before ':' or at end).
 * Does NOT remove parentheses that are part of the key name (e.g., "Expansion (Control) Joint:").
 *
 * @param text - The text to strip designation from
 * @param serverDesignation - When available, enables exact pattern matching instead of regex heuristics
 * @returns The text with designation removed, or the original text/undefined if no match
 */
export const stripDesignationFromText = (
    text: string | undefined,
    serverDesignation?: string
): string | undefined => {
    if (!text) return text

    // Empty string means no previous designation — nothing to strip
    if (serverDesignation === '') return text

    // When server designation is available, use exact pattern matching
    // This handles any designation content (nested parens, colons, special chars)
    if (serverDesignation) {
        const designationPattern = `(${serverDesignation})`
        const patternIndex = text.lastIndexOf(designationPattern)

        if (patternIndex !== -1) {
            const prefix = text.substring(0, patternIndex).trim()
            const afterPattern = text.substring(
                patternIndex + designationPattern.length
            )
            return prefix + afterPattern
        }

        // Designation not in this element — return as-is
        return text
    }

    // Fallback: regex-based stripping for cases without server designation
    // Matches simple "(DD-1):" or "(Value)" at end, but NOT "Expansion (Control) Joint:"
    const cleaned = text.replace(/\s*\([^)]*\)\s*(?=:|\s*$)/, '')

    // If regex matched something, return cleaned result
    if (cleaned !== text) return cleaned

    // Fallback: handle unbalanced/malformed parentheses
    // Only cut from first '(' if there's NO colon after it (to preserve keys like "Expansion (Control) Joint:")
    const firstParen = text.indexOf('(')
    if (firstParen === -1) return text

    // Check if there's a colon after the first parenthesis - if so, it's likely a key name, don't cut
    const colonAfterParen = text.indexOf(':', firstParen)
    if (colonAfterParen !== -1) return text

    // No colon after '(', safe to cut (handles malformed designations)
    const cutPoint =
        firstParen > 0 && text[firstParen - 1] === ' '
            ? firstParen - 1
            : firstParen
    return text.substring(0, cutPoint)
}
